import { derived, writable } from "svelte/store";
import { SwApiStore } from "../../../api-stores/sw-api-store";
import { usersStore } from "../../../api-stores/user-store";
import { getDiallerToken } from "../../../api/dialler";
import { diallerState } from "./diallerState.svelte";
import { twilioManager } from "./utils/TwilioManager";
import { cleanupInitialization } from "./utils/initialization";

// Token store - no caching to match legacy behavior (fetch fresh token for each call)
export const diallerTokenStore = SwApiStore({
  get: (opts) => getDiallerToken(opts),
  init: () => ({ token: null, expires_at: null }),
  cacheKey: "dialler-token",
  cacheDurationMs: 0, // No caching - fetch fresh token each time
});

// Call outcomes
export const callOutcomesStore = writable([
  { id: "interested", name: "Interested" },
  { id: "booked", name: "Meeting scheduled" },
  { id: "noAnswer", name: "No Answer" },
  { id: "notInterested", name: "Not Interested" },
  { id: "callBackLater", name: "Call back later" },
]);

// Derived store for formatted outcomes
export const outcomeOptionsStore = derived(callOutcomesStore, ($outcomes) =>
  $outcomes
    .sort((a, b) => (a.order || 999) - (b.order || 999))
    .map((outcome) => ({
      id: outcome.id,
      label: outcome.name,
      color: outcome.color,
    })),
);

export const initializeStores = () => {
  // Stores are already initialized via imports
};

let isRequestingPermission = false;

export const requestMicrophonePermission = async () => {
  if (isRequestingPermission) {
    return false;
  }

  isRequestingPermission = true;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());

    diallerState.microphonePermission = "granted";

    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((d) => d.kind === "audioinput");
    const outputs = devices.filter((d) => d.kind === "audiooutput");

    diallerState.updateAvailableDevices({ inputs, outputs });

    return true;
  } catch (error) {
    if (
      error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError"
    ) {
      diallerState.microphonePermission = "denied";
    }
    return false;
  } finally {
    isRequestingPermission = false;
  }
};

export const getVoicemailsStore = () => diallerState.voicemailsStore;

export { loadDiallerData } from "./utils/initialization";

export const cleanupDiallerStores = async () => {
  await twilioManager.destroy();
  cleanupInitialization();
};
//TODO add filters for those with licenses
export const diallerUsersStore = derived(
  [usersStore, usersStore.meta],
  ([$usersStore, $meta]) =>
    $meta.fetching
      ? []
      : $usersStore.map((user) => ({
          name: user.name,
          firstName: user.firstName,
          lastName: user.lastName,
          id: user.userIds.sort().join(","),
          phone: user.phone,
        })),
);

export const getUserNameFromId = (userId, usersData) => {
  const user = usersData.find(
    (user) => user.userIds && user.userIds.includes(userId),
  );

  return user?.name || null;
};
